import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Package,
  Tags,
  Disc,
  DatabaseBackup,
  NotebookPen,
  ShieldAlert,
  BookText,
  FolderOpenDot,
  Link2,
  ClipboardList,
  CalendarDays,
  ScrollText,
  Calculator,
  Shield,
  ShieldCheck,
  KeySquare,
  LogOut,
  Plus,
  Menu,
  X,
  KeyRound,
  Eye,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { toast } from "sonner";
import { RHEEL_LOGO_URL } from "@/constants/brand";

const BASE_NAV = [
  { to: "/", label: "Overview", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/assets", label: "Assets", icon: Package, testid: "nav-assets" },
  { to: "/accounting", label: "Accounting", icon: Calculator, testid: "nav-accounting" },
  { to: "/categories", label: "Categories", icon: Tags, testid: "nav-categories" },
  { to: "/software", label: "Software", icon: Disc, testid: "nav-software" },
  { to: "/backups", label: "Backups", icon: DatabaseBackup, testid: "nav-backups" },
  { to: "/notebook", label: "Notebook", icon: NotebookPen, testid: "nav-notebook" },
  { to: "/documentation", label: "Documentation", icon: BookText, testid: "nav-documentation" },
  { to: "/site-docs", label: "Site Docs", icon: FolderOpenDot, testid: "nav-site-docs" },
  { to: "/links-info", label: "Links & Info", icon: Link2, testid: "nav-links-info" },
  { to: "/dr-plan", label: "DR Plan", icon: ShieldAlert, testid: "nav-dr-plan", danger: true },
  { to: "/forms", label: "Forms", icon: ClipboardList, testid: "nav-forms" },
  { to: "/service-work", label: "Service Work", icon: CalendarDays, testid: "nav-service-work" },
  { to: "/hawke-eye", label: "365 Hawke Eye", icon: Eye, testid: "nav-hawke-eye" },
  { to: "/security", label: "Security", icon: ShieldCheck, testid: "nav-security" },
];

const ADMIN_NAV = [
  { to: "/audit", label: "Audit Log", icon: ScrollText, testid: "nav-audit" },
  { to: "/licenses", label: "M365 Licenses", icon: KeySquare, testid: "nav-licenses" },
  { to: "/adobe-licenses", label: "Adobe Licenses", icon: KeySquare, testid: "nav-adobe" },
  { to: "/admin", label: "Admin", icon: Shield, testid: "nav-admin" },
];

export default function Layout() {
  const { user, logout, switchOrg } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false); // mobile sidebar open
  const [pwOpen, setPwOpen] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "" });
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  // Service Work is only visible from the Rheel Group – Internal org (Rheel's own operational calendar)
  const activeOrgName = (user?.organization_name || "").replace(/[–—]/g, "-").toLowerCase().trim();
  const isRheelInternal = activeOrgName === "rheel group - internal";
  const FILTERED_BASE = BASE_NAV.filter((n) => (n.to !== "/service-work" && n.to !== "/hawke-eye") || isRheelInternal);
  const NAV = isAdmin ? [...FILTERED_BASE, ...ADMIN_NAV] : FILTERED_BASE;
  const orgList = [...(user?.organization_names || [])].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  const canSwitch = orgList.length > 1;

  const submitChangePassword = async () => {
    if (pwForm.new_password.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    try {
      await api.post("/auth/change-password", pwForm);
      toast.success("Password changed");
      setPwOpen(false);
      setPwForm({ current_password: "", new_password: "" });
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const sidebar = (
    <aside
      className="border-r border-[#E5E0D5] flex flex-col bg-[#FAF7F0] h-full w-64"
      data-testid="sidebar"
    >
      <div className="px-6 py-6 border-b border-[#E5E0D5] flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src={RHEEL_LOGO_URL} alt="Rheel Group" className="h-11 w-auto object-contain" />
          <div>
            <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#7A7A7A]">MSP Dashboard</div>
            <div className="font-mono text-xs text-[#1A1A1A]">v1.0</div>
          </div>
        </div>
        <button
          className="lg:hidden p-2 text-[#5A5A5A] hover:text-[#0F0F0F]"
          onClick={() => setOpen(false)}
          aria-label="Close menu"
          data-testid="close-menu"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="px-4 py-3 border-b border-[#E5E0D5]">
        <div className="uppercase-label mb-1">Organization</div>
        {canSwitch ? (
          <select
            data-testid="org-switcher"
            value={user?.organization_id || ""}
            onChange={async (e) => {
              const res = await switchOrg(e.target.value);
              if (res.ok) {
                setOpen(false);
                navigate("/");
              }
            }}
            className="w-full font-mono text-sm text-[#0F0F0F] bg-[#F0EDE3] border border-[#D6CFC1] px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#1A1A1A]"
          >
            {orgList.map((o) => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
        ) : (
          <div className="font-mono text-sm text-[#0F0F0F] truncate" data-testid="current-org">{user?.organization_name || "—"}</div>
        )}
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            data-testid={item.testid}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-3 text-sm transition-colors duration-150 border-l-2 ${
                item.danger
                  ? (isActive
                      ? "border-[#B91C1C] bg-[#FEF2F2] text-[#B91C1C]"
                      : "border-transparent text-[#B91C1C] hover:bg-[#FEF2F2] hover:text-[#a11818]")
                  : (isActive
                      ? "border-[#F37B2A] bg-[#F0EDE3] text-[#0F0F0F]"
                      : "border-transparent text-[#5A5A5A] hover:text-[#0F0F0F] hover:bg-[#F0EDE3]")
              }`
            }
          >
            <item.icon className="w-4 h-4" strokeWidth={1.5} />
            <span className="font-mono text-xs tracking-wider uppercase">{item.label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="px-3 py-4 border-t border-[#E5E0D5]">
        <Button
          data-testid="new-asset-quick-btn"
          onClick={() => { setOpen(false); navigate("/assets/new"); }}
          className="w-full rounded-none bg-[#F37B2A] hover:bg-[#D86A1F] text-white font-mono text-xs tracking-wider uppercase h-11"
        >
          <Plus className="w-4 h-4 mr-2" strokeWidth={2} />
          New Asset
        </Button>
      </div>

      <div className="px-6 py-4 border-t border-[#E5E0D5]">
        <div className="uppercase-label mb-1">Operator</div>
        <div className="font-mono text-xs text-[#3A3A3A] truncate" data-testid="current-user-email">{user?.email}</div>
        <div className="font-mono text-[10px] text-[#7A7A7A] mt-0.5 uppercase tracking-wider">{user?.role?.replace("_", " ")}</div>
        <div className="mt-3 flex flex-col gap-2">
          <button
            data-testid="change-password-btn"
            onClick={() => { setPwOpen(true); setOpen(false); }}
            className="inline-flex items-center gap-2 text-xs font-mono tracking-wider uppercase text-[#7A7A7A] hover:text-[#0F0F0F] transition-colors"
          >
            <KeyRound className="w-3.5 h-3.5" strokeWidth={1.5} />
            Change Password
          </button>
          <button
            data-testid="logout-btn"
            onClick={async () => { await logout(); navigate("/login"); }}
            className="inline-flex items-center gap-2 text-xs font-mono tracking-wider uppercase text-[#7A7A7A] hover:text-[#B91C1C] transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" strokeWidth={1.5} />
            Sign Out
          </button>
        </div>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen bg-[#FAF7F0] text-[#0F0F0F] lg:grid lg:grid-cols-[240px_1fr]">
      {/* Mobile top bar */}
      <header className="lg:hidden sticky top-0 z-30 flex items-center justify-between bg-[#FAF7F0] border-b border-[#E5E0D5] px-4 h-14">
        <button
          onClick={() => setOpen(true)}
          className="p-2 text-[#3A3A3A] hover:text-[#0F0F0F]"
          aria-label="Open menu"
          data-testid="open-menu"
        >
          <Menu className="w-6 h-6" />
        </button>
        <img src={RHEEL_LOGO_URL} alt="Rheel Group" className="h-8 w-auto object-contain" />
        <button
          onClick={() => navigate("/assets/new")}
          className="p-2 text-[#1A1A1A] hover:text-black"
          aria-label="New Asset"
          data-testid="mobile-new-asset"
        >
          <Plus className="w-6 h-6" />
        </button>
      </header>

      {/* Desktop sidebar */}
      <div className="hidden lg:block">{sidebar}</div>

      {/* Mobile slide-in sidebar */}
      {open && (
        <div
          className="lg:hidden fixed inset-0 z-40 flex"
          onClick={() => setOpen(false)}
        >
          <div onClick={(e) => e.stopPropagation()} className="h-full">
            {sidebar}
          </div>
          <div className="flex-1 bg-black/60 backdrop-blur-sm" />
        </div>
      )}

      {/* Main content */}
      <main className="overflow-auto" key={user?.organization_id || "no-org"}>
        <Outlet />
      </main>

      {/* Change password dialog */}
      {pwOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={() => setPwOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white border border-[#D6CFC1] w-full max-w-md" data-testid="change-pw-dialog">
            <div className="px-5 py-3 border-b border-[#E5E0D5]">
              <div className="uppercase-label">Change Password</div>
              <div className="font-mono text-sm text-[#0F0F0F] mt-1 truncate">{user?.email}</div>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <Label className="uppercase-label">Current Password</Label>
                <Input
                  data-testid="current-pw-input"
                  type="password"
                  value={pwForm.current_password}
                  onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })}
                  className="mt-2 rounded-none bg-white border-[#D6CFC1] font-mono text-sm focus-visible:ring-1 focus-visible:ring-[#F37B2A]"
                />
              </div>
              <div>
                <Label className="uppercase-label">New Password</Label>
                <Input
                  data-testid="new-pw-input"
                  type="password"
                  minLength={6}
                  value={pwForm.new_password}
                  onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })}
                  placeholder="At least 6 characters"
                  className="mt-2 rounded-none bg-white border-[#D6CFC1] font-mono text-sm focus-visible:ring-1 focus-visible:ring-[#F37B2A]"
                />
              </div>
            </div>
            <div className="px-5 py-3 border-t border-[#E5E0D5] flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setPwOpen(false)} className="rounded-none border-[#D6CFC1] bg-transparent text-[#3A3A3A] hover:bg-[#F0EDE3] font-mono text-xs tracking-wider uppercase">Cancel</Button>
              <Button type="button" onClick={submitChangePassword} data-testid="confirm-change-pw" className="rounded-none bg-[#F37B2A] hover:bg-[#D86A1F] text-white font-mono text-xs tracking-wider uppercase">Update Password</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
